import { useMemo } from 'react';
import Image from 'next/image';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';

import { Title } from '@restworld/ui-ds';
import { customImageLoader } from '@restworld/utils-common';
import { JobApplicationStatusKey } from '@restworld/utility-types';

import { useKanbanRowsAndMatchStatusKeys } from '../../../hooks';
import { candidateModalVariantAtom, selectedJobApplicationAtom } from '../../../employer-job-listing.atoms';

function CandidatePresentationNote() {
  const [selectedJobApplication] = useAtom(selectedJobApplicationAtom);
  const [candidateModalVariant] = useAtom(candidateModalVariantAtom);
  const isSpontaneousApplication = useMemo(
    () => !selectedJobApplication?.job_position_id,
    [selectedJobApplication?.job_position_id]
  );

  const candidateNotesProps = useMemo(
    () => ({
      jobApplicationStatusKey: selectedJobApplication?.status_key,
      discardedFromStatusKey: selectedJobApplication?.discarded_from_status_key,
      discardedBy: selectedJobApplication?.discarded_by,
      discardedOtherReason: selectedJobApplication?.discarded_other_reason,
      discardedReason: selectedJobApplication?.discarded_reason_var?.label,
      notes: selectedJobApplication?.worker_presentation?.presentation || '',
      csmImage: selectedJobApplication?.is_contacted_by_rw_member?.icon_url,
      coverLetter: selectedJobApplication?.cover_letter,
      evaluationAnalysis: selectedJobApplication?.evaluation?.evaluation_analysis,
      addedToShortlistAt: selectedJobApplication?.added_to_shortlist_at,
    }),
    [
      selectedJobApplication?.added_to_shortlist_at,
      selectedJobApplication?.cover_letter,
      selectedJobApplication?.discarded_by,
      selectedJobApplication?.discarded_from_status_key,
      selectedJobApplication?.discarded_other_reason,
      selectedJobApplication?.discarded_reason_var?.label,
      selectedJobApplication?.evaluation?.evaluation_analysis,
      selectedJobApplication?.is_contacted_by_rw_member?.icon_url,
      selectedJobApplication?.status_key,
      selectedJobApplication?.worker_presentation?.presentation,
    ]
  );
  const isDiscarded = useMemo(
    () =>
      candidateNotesProps?.jobApplicationStatusKey === 'worker_jp_discarded' ||
      // Treat pre-shortlist self-withdrawals as part of the "preselection rejected" view (Non idonei tab)
      (candidateNotesProps?.jobApplicationStatusKey === 'worker_jp_not_interested' &&
        !candidateNotesProps?.addedToShortlistAt),
    [candidateNotesProps?.addedToShortlistAt, candidateNotesProps?.jobApplicationStatusKey]
  );
  const isSpontaneousCandidate = useMemo(
    () => candidateModalVariant === 'spontaneous_candidate' || candidateModalVariant === 'spontaneous_candidate_notes',
    [candidateModalVariant]
  );

  return isSpontaneousCandidate || isSpontaneousApplication ? (
    <CoverLetter {...candidateNotesProps} />
  ) : isDiscarded ? (
    <DiscardedCandidateNote {...candidateNotesProps} />
  ) : (
    <ActiveShortListedCandidateNote {...candidateNotesProps} />
  );
}
export default CandidatePresentationNote;

function ActiveShortListedCandidateNote({ evaluationAnalysis, csmImage }: CandidateNotesProps) {
  return (
    <MainWrapper>
      <ImageAndTitleWrapper>
        {csmImage && (
          <ImageWrapper>
            <Image src={csmImage} alt="" fill loader={customImageLoader} tw="object-cover object-center" />
          </ImageWrapper>
        )}
      </ImageAndTitleWrapper>
      <NoteAndButtonWrapper>
        <EvaluationWrapper>
          <EvaluationContent dangerouslySetInnerHTML={{ __html: evaluationAnalysis }} />
        </EvaluationWrapper>
      </NoteAndButtonWrapper>
    </MainWrapper>
  );
}

function DiscardedCandidateNote({
  discardedFromStatusKey,
  discardedBy,
  discardedOtherReason,
  discardedReason,
  evaluationAnalysis,
  addedToShortlistAt,
}: CandidateNotesProps) {
  const { t: t } = useTranslation('job-positions-page');

  const kanbanRowStatusKeys = useKanbanRowsAndMatchStatusKeys();

  const kanbanRowWhichCandidateWasIn = useMemo(
    () =>
      kanbanRowStatusKeys.find((v) => v.matchStatusKey.includes(discardedFromStatusKey as JobApplicationStatusKey))
        ?.title,
    [discardedFromStatusKey, kanbanRowStatusKeys]
  );

  const isPreselectionRejected = !addedToShortlistAt;

  if (isPreselectionRejected) {
    // For preselection-rejected candidates we only render the grey box when the AI
    // actually produced an analysis (Cases 1 + 2). For all the other variants
    // (withdrew, preassessment incomplete with no analysis, hard-rule rejection)
    // the contextual reason is surfaced via the orange alert in `ConstraintAlerts`
    // — showing an empty box would just add noise.
    if (!evaluationAnalysis) return null;

    return (
      <MainWrapper variant="discarded">
        <EvaluationContent dangerouslySetInnerHTML={{ __html: evaluationAnalysis }} />
      </MainWrapper>
    );
  }

  return (
    <MainWrapper variant="discarded">
      {!!kanbanRowWhichCandidateWasIn && (
        <div tw="mb-3">
          <Title fontSize="small" fontWeight="medium" textColor="neutral-200" twStyle={tw`text-sm`}>
            {t('was_in')}
          </Title>
          <Title fontSize="small" fontWeight="medium" textColor="neutral-700" twStyle={tw`text-sm`}>
            {kanbanRowWhichCandidateWasIn}
          </Title>
        </div>
      )}
      {!!discardedBy && (
        <div tw="mb-3">
          <Title fontSize="small" fontWeight="medium" textColor="neutral-200" twStyle={tw`text-sm`}>
            {t('discarded_by')}
          </Title>
          <Title fontSize="small" fontWeight="medium" textColor="neutral-700" twStyle={tw`text-sm`}>
            {discardedBy.split('_').join(' ')}
          </Title>
        </div>
      )}
      <div>
        <Title fontSize="small" fontWeight="medium" textColor="neutral-200" twStyle={tw`text-sm`}>
          {t('reason')}
        </Title>
        <Title fontSize="small" fontWeight="bold" textColor="neutral-700" twStyle={tw`text-sm`}>
          {discardedReason}
        </Title>
        {!!discardedOtherReason && (
          <Title fontSize="small" fontWeight="medium" textColor="neutral-700" twStyle={tw`text-sm`}>
            {discardedOtherReason}
          </Title>
        )}
      </div>
    </MainWrapper>
  );
}

function CoverLetter({ coverLetter }: CandidateNotesProps) {
  const { t: t } = useTranslation();

  return (
    <MainWrapper variant="spontaneous">
      <NoteAndButtonWrapper>
        <EvaluationWrapper>
          {coverLetter ? coverLetter : t('common:this_candidate_did_not_upload_a_cover_letter_with_the_application')}
        </EvaluationWrapper>
      </NoteAndButtonWrapper>
    </MainWrapper>
  );
}

const MainWrapper = styled.div<{ variant?: 'spontaneous' | 'discarded' | 'default' }>`
  ${tw`rounded-lg px-4 py-3 lg:(mx-0) min-h-[6.5rem] overflow-hidden overflow-y-auto`}
  ${({ variant }) => (variant === 'discarded' ? tw`bg-neutral-30` : tw`bg-purple-50`)}
`;
const ImageAndTitleWrapper = styled.div`
  ${tw`mb-4 flex gap-2 items-center`}
`;
const ImageWrapper = styled.div`
  ${tw` border border-purple-700 rounded-full bg-purple-300 relative w-8 h-8 min-w-[2rem] flex items-center justify-center object-cover object-center overflow-hidden`}
`;
const EvaluationWrapper = styled.div`
  ${tw`text-neutral-700 text-base w-full`}
`;
const NoteAndButtonWrapper = styled.div`
  ${tw`flex flex-col items-center gap-4`}
`;
const EvaluationContent = styled.div`
  ${tw`font-light text-base`}

  h4 {
    ${tw`text-base font-bold italic mt-5 mb-2 first:mt-0`}
  }

  ul {
    ${tw`list-disc list-outside ml-5 mb-4 last:mb-4`}
  }

  strong {
    ${tw`font-medium`}
  }
`;

interface CandidateNotesProps {
  evaluationAnalysis?: string;
  discardedFromStatusKey?: string;
  discardedBy?: string;
  discardedReason?: string;
  discardedOtherReason?: string;
  jobApplicationStatusKey?: JobApplicationStatusKey;
  csmImage?: string;
  coverLetter?: string;
  addedToShortlistAt?: string | null;
}
